import { parseISO, subHours, isAfter, isValid } from 'date-fns';
import { Game, GameFacility } from '../types/models/Game';

/**
 * Verifica si estamos dentro del período de cancelación automática de un partido.
 */
export function isWithinGameCancelationPeriod(
    bookingIni: string,
    gameCancelation: number | null,
) {
    if (!bookingIni) {
        return false;
    }
    if (!gameCancelation || gameCancelation <= 0) {
        return false;
    }

    const now = new Date();
    const bookingStart = parseISO(bookingIni);
    if (!isValid(bookingStart)) {
        return false;
    }

    const cancelationDeadline = subHours(bookingStart, gameCancelation);

    return isAfter(now, cancelationDeadline);
}

/**
 * Determina si una instalación con partido debe usar tarifas completas
 * en lugar de individuales debido al período de cancelación.
 */
export function shouldForceFullRatesForGame(
    facility: GameFacility,
    bookingIni: string,
) {
    if (!facility || !facility.isForGame) {
        return false;
    }

    return isWithinGameCancelationPeriod(bookingIni, facility.gameCancelation);
}

/**
 * Determina si se pueden agregar más jugadores a un partido
 * basado en si fue creado durante el periodo de cancelación.
 */
export function canAddMorePlayersToGame(game: Game): boolean {
    // Validaciones básicas
    if (!game || !game.facility || !game.facility.isForGame) {
        return true;
    }

    // Si la instalación no tiene periodo de cancelación, permitir inscripciones normales
    if (!game.facility.gameCancelation || game.facility.gameCancelation <= 0) {
        return true;
    }

    // Si no hay información de la cola de cancelación, usar lógica por defecto (permitir)
    if (!game.cancelationQueue) {
        return true;
    }

    const { inQueue } = game.cancelationQueue;
    const hasPlayers = Array.isArray(game.players) && game.players.length > 0;

    // Si está en la cola de cancelación, fue creado FUERA del periodo → permitir inscripciones
    if (inQueue) {
        return true;
    }

    // Si NO está en la cola, fue creado DENTRO del periodo (tarifa completa forzada)
    // → Bloquear si ya hay jugadores
    return !hasPlayers;
}

/**
 * Determina si un partido fue creado con tarifa completa forzada
 * (es decir, creado durante el periodo de cancelación).
 */
export function wasCreatedWithFullRateForced(game: Game): boolean {
    // Validaciones básicas
    if (!game || !game.facility || !game.facility.isForGame) {
        return false;
    }

    // Si no hay periodo de cancelación configurado, no puede haber sido creado con tarifa forzada
    if (!game.facility.gameCancelation || game.facility.gameCancelation <= 0) {
        return false;
    }

    // Si no hay información de la cola de cancelación, asumimos que no fue con tarifa forzada
    if (!game.cancelationQueue) {
        return false;
    }

    // Si no está en la cola de cancelación (inQueue === false), fue creado con tarifa completa forzada
    return game.cancelationQueue.inQueue === false;
}

/**
 * Traduce la opción elegida en HandlePaymentsModal ('0'..'4') al código
 * `paymentAction` (DELETING_PAYMENT_ACTION en sporttia-service) que espera
 * DELETE /games/{id}. Reusa el mismo mapeo que cancelar una reserva.
 *
 * Diferencia con reservas: para "no hacer nada" se envía un 0 (NOTHING)
 * EXPLÍCITO en lugar de omitir el campo, porque deleteGameById convierte un
 * paymentAction nulo en PAID_PURSE_PENDING_DELETE (el default del cron). Como
 * el backend usa `??`, el 0 se preserva y los pagos quedan intactos.
 */
export function cancelGameOptionToPaymentAction(option: string): number | null {
    switch (option) {
        case '0':
            return 0; // NOTHING — dejar los pagos como están
        case '1':
            return 4; // REFUND_PURSE — devolver al monedero
        case '2':
            return 5; // REFUND_CASH — devolver al contado
        case '3':
            return 2; // DELETE_ALL — eliminar pagos asociados
        case '4':
            return 3; // DELETE_ONLY_PENDING — dejar pendientes
        default:
            return null;
    }
}
